import React, { useState, useMemo, useEffect, useRef, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { useModularApprovalTracking } from '../../../hooks/useModularApprovalTracking';
import { usePendingApprovals } from '../../../hooks/usePendingApprovals';
import { finalizeModularSectionReview } from '../../../utils/sectionReviewFinalization';
import { computeUnpersistedApprovals } from '../../../utils/approvalStateUtils';
import { toast } from 'react-toastify';
import { useFloatingActionPanel } from '../11_reviewDocuments/components/floatingPanel/useFloatingActionPanel';
import { FloatingActionPanel } from '../11_reviewDocuments/components/floatingPanel/FloatingActionPanel';
import { InitialWarningDialog } from '../11_reviewDocuments/components/InitialWarningDialog';
import WarningBar from '../11_reviewDocuments/components/WarningBar';
import DocumentReviewHeader from '../11_reviewDocuments/components/DocumentReviewHeader';
import { decodeHtmlEntities } from '../11_reviewDocuments/functions/htmlEncoders';
import {
  handlePlainTextPaste,
  suppressFormattingShortcuts,
} from '../11_reviewDocuments/functions/contentEditableUtils';
import { renderModelInput, compareStructuredEntryKeys, omitCypSsiEnvironmentMet } from '../../../utils/renderModelInput';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { STAGE_TO_CLINICIAN_KEY } from '../../../constants/cypAdhdStages';
import { appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { TriggerRenderCypAdhdDocTemplate } from '../../../utils/TriggerRenderCypAdhdDocTemplate';
import { rerenderCypAdhdPatientReportIfStale } from '../../../utils/autoCypAdhdPatientReport';
import { TriggerSSICompletionEmail } from '../11_reviewDocuments/functions/TriggerSSICompletionEmail';
import { TriggerPADCompletionEmail } from '../11_reviewDocuments/functions/TriggerPADCompletionEmail';
import { db, auth } from '../../../firebase';
import { doc as firestoreDoc, updateDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { getCypAdhdReportContentPathSegments } from '../../../constants/cypAdhdFirestorePaths';
import '../11_reviewDocuments/styles/ReviewDocuments.css';
import {
  emitParagraphApproval,
  emitParagraphEdited,
  emitParagraphReset,
} from '../../../analytics/reportEvents';

/**
 * CypAdhdReviewDocument — shared per-stage review and approval page for the
 * CYP ADHD pathway (ASMT-21).
 *
 * One parameterised component instead of the per-stage near-duplicates the
 * autism pathway carries (RD_CI_Autism.js and siblings): the CYP stages
 * differ only in their document structure, stage keys, and AI-input panel
 * groups, so the thin wrappers (RD_CI_CypAdhd, RD_DD_CypAdhd) supply those
 * and this component owns the behaviour. The behaviour itself mirrors
 * RD_CI_Autism paragraph for paragraph: floating action panel, AI-inputs
 * side panel, approve/edit/skip/reset, targeted per-key updateDoc writes
 * (no read-modify-write contention), snapshot-derived finalisation.
 *
 * APPROVALS ARE LOAD-BEARING: cyp_adhd_stage_progress.py computes DD lock
 * state from ciReportApproved + ssiReportApproved, and PAD from the DD
 * stage's completion. finalizeModularSectionReview writes exactly those
 * flags (flagPrefix 'ci' / 'ssi' / 'dd' / 'pad') onto the CYP reportContent
 * doc via the CYP path builder.
 *
 * PARITY WITH AUTISM:
 *  - Document render trigger on exit: on full approval + dirty session,
 *    fires TriggerRenderCypAdhdDocTemplate for the current stage's CAR.
 *  - Patient-report auto-finalise: fires rerenderCypAdhdPatientReportIfStale
 *    on full approval — gates on all four section flags (CI/SSI/DD/PAD),
 *    checks staleness against the trigger-time anchor on the patientReport
 *    reportContent doc, and handles wait-then-retry for concurrent renders.
 *
 * @param {Object}   props
 * @param {string}   props.stageKey        reportContent doc key
 *                                          ('clinicalInterview' | 'diagnosticDecision' | ...)
 * @param {string}   props.flagPrefix      approval flag prefix ('ci' | 'dd' | ...)
 * @param {string}   props.moduleTitle     human title ("Clinical Interview")
 * @param {string}   props.editStageCode   useCanEditStage stage code ('CI' | 'DD')
 * @param {Function} props.buildDocStructure (reportDoc) => docStructure —
 *                    section title -> { n: { title, code } }. Receives the
 *                    stage's reportContent doc so structures can be dynamic
 *                    (the DD secondary-condition list).
 * @param {Array}    props.inputPanelGroups [[label, inputsKey], ...] groups
 *                    rendered in the AI Inputs side panel, in order.
 */
export default function CypAdhdReviewDocument({
  stageKey,
  flagPrefix,
  moduleTitle,
  editStageCode,
  buildDocStructure,
  inputPanelGroups,
}) {
  const { patientId } = useParams();
  const navigate = useNavigate();

  const {
    header,
    reportContent,
    refreshReportContent,
    clearPatientData,
    fetchPatientData,
    loadedPatientId,
  } = useContext(ModularPatientDataContext);

  // Only the clinician assigned as primary for this stage may review, edit,
  // and approve. Everyone else who can open the page (org admins, the DD
  // supporting clinician) lands read-only — a deep link must not become an
  // editing back door.
  const canEdit = useCanEditStage(header || {}, editStageCode);

  const assignedClinicianName =
    header?.clinicians?.[STAGE_TO_CLINICIAN_KEY[stageKey]]?.clinicianName;

  useEffect(() => {
    fetchPatientData(patientId);
  }, [patientId, fetchPatientData]);

  const reportDoc = reportContent?.[stageKey];
  const REPORT_DATA_OUTPUTS = useMemo(() => reportDoc?.outputs || {}, [reportDoc]);
  const REPORT_EDIT_HISTORY = useMemo(() => reportDoc?.editHistory || {}, [reportDoc]);

  const docStructure = useMemo(
    () => buildDocStructure(reportDoc || {}),
    [buildDocStructure, reportDoc],
  );

  // ── UI state ───────────────────────────────────────────────────────────
  const [state, setState] = useState({
    selectedCode: null,
    isSidePanelOpen: false,
    modelInputsFirestore: null,
    panelInputs: {},
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showInitialWarning, setShowInitialWarning] = useState(true);
  const [initialContent, setInitialContent] = useState({});
  const [preEditContent, setPreEditContent] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  // Session-dirty flag, held in a REF and read at exit time. State was the
  // original shape (matching autism), but the exit handler AWAITS pending
  // approvals (settlePendingApprovals) whose completion is what sets the
  // flag — a state value is captured by the exit's closure BEFORE that await
  // resolves, so an approval settled during exit was invisible to the render
  // gate and the document was silently not regenerated. A ref is read at the
  // moment of the decision instead. (Found by the render-gating test, which
  // reproduced the same race by approving and exiting in one breath.)
  const sectionDirtyRef = useRef(false);
  const markSectionDirty = () => { sectionDirtyRef.current = true; };

  // ── Approval tracking ──────────────────────────────────────────────────
  const {
    totalParagraphs, reviewedCodes, isFullyApproved,
    showWarning, addReviewedCode, removeReviewedCode,
  } = useModularApprovalTracking({
    docStructure,
    reportDataOutputs: REPORT_DATA_OUTPUTS,
    reportDataApproved: reportDoc?.approvedParagraphs,
  });

  const { track: trackApproval, settleAll: settlePendingApprovals } = usePendingApprovals();

  // ── Refs and floating panel ────────────────────────────────────────────
  const editableContentRef = useRef(null);
  const documentContainerRef = useRef(null);

  const { panelRef, panelPosition, safeUpdatePosition } = useFloatingActionPanel({
    containerRef: documentContainerRef,
    selectedCode: state.selectedCode,
    isEditing,
    topOffset: -15,
  });

  // ── Paragraph selection ────────────────────────────────────────────────
  const handleParagraphClick = (event, code) => {
    setIsEditing(false);
    if (!state.modelInputsFirestore) {
      console.error('modelInputsFirestore is not populated');
      return;
    }
    if (code && state.modelInputsFirestore[code]) {
      const inputs = state.modelInputsFirestore[code];
      setState(prevState => ({
        ...prevState,
        selectedCode: code,
        panelInputs: inputs,
        isSidePanelOpen: true,
      }));
      setTimeout(() => safeUpdatePosition(code), 0);
    } else {
      console.error('No matching input found for the code:', code);
    }
  };

  // ── Section rendering ──────────────────────────────────────────────────
  const Section = ({ title, paragraphs }) => {
    const availableParagraphs = Object.entries(paragraphs).filter(
      ([, { code }]) => REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== ''
    );
    if (availableParagraphs.length === 0) return null;

    return (
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4 text-blue-500">{title}</h2>
        {availableParagraphs.map(([key, { title: subTitle, code }]) => {
          const content = REPORT_DATA_OUTPUTS[code];
          const decodedContent = decodeHtmlEntities(content);
          return (
            <div key={key} className="mb-4">
              {subTitle && (
                <h3 className="font-semibold mb-2 text-gray-900">{subTitle}</h3>
              )}
              <div
                data-paragraph-container
                className={`
                relative text-sm font-medium p-2 rounded-lg
                transition-all duration-200
                ${isEditing && state.selectedCode === code
                    ? 'bg-amber-100 z-20'
                    : reviewedCodes.has(code)
                      ? 'bg-green-600/30 hover:bg-green-600/40 approved-paragraph'
                      : 'bg-violet-100 hover:bg-violet-200'
                  }
                ${state.selectedCode === code ? 'ring ring-violet-800' : ''}
                ${!isEditing ? 'cursor-pointer' : ''}
              `}
                id={code}
                onClick={(e) => {
                  if (!isEditing) handleParagraphClick(e, code);
                }}
              >
                <p
                  ref={editableContentRef}
                  className="text-gray-700 leading-relaxed whitespace-pre-wrap"
                  contentEditable={isEditing && state.selectedCode === code}
                  onKeyDown={suppressFormattingShortcuts}
                  onInput={(e) => {
                    if (isEditing && state.selectedCode === code) {
                      const newContent = e.target.innerText;
                      if (REPORT_DATA_OUTPUTS) {
                        REPORT_DATA_OUTPUTS[code] = newContent;
                      }
                      safeUpdatePosition(code);
                    }
                  }}
                  onPaste={handlePlainTextPaste}
                  suppressContentEditableWarning={true}
                >
                  {decodedContent}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Edit handlers ──────────────────────────────────────────────────────
  const handleStartEdit = () => {
    if (!canEdit) return;
    if (state.selectedCode) {
      setPreEditContent(prev => ({
        ...prev,
        [state.selectedCode]: REPORT_DATA_OUTPUTS[state.selectedCode] || '',
      }));
    }
    setIsEditing(true);
    setTimeout(() => {
      const paragraphElement = document.querySelector(`#${state.selectedCode} p`);
      if (paragraphElement) {
        paragraphElement.focus();
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(paragraphElement);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      safeUpdatePosition(state.selectedCode);
    }, 0);
  };

  const handleSaveEdit = () => {
    if (!canEdit) {
      toast.error(`Only the clinician assigned to the ${moduleTitle} module can edit these paragraphs.`);
      return;
    }
    if (!state.selectedCode) return;

    const spanCode = state.selectedCode;
    const newValue = REPORT_DATA_OUTPUTS[spanCode];
    const previousValue = initialContent[spanCode] || '';

    if (newValue === previousValue) {
      setIsEditing(false);
      safeUpdatePosition(spanCode);
      return;
    }

    removeReviewedCode(spanCode);
    setIsEditing(false);
    safeUpdatePosition(spanCode);

    const editEntry = {
      clinicianId: auth.currentUser.uid,
      clinicianName: auth.currentUser.displayName,
      timestamp: serverTimestamp(),
      previousValue,
      newValue,
      editType: 'manual',
    };
    const timestampKey = Date.now().toString();

    // Targeted per-key updateDoc (no read-modify-write transaction) on the
    // CYP reportContent subcollection doc — same slow-network rationale as
    // the autism pages.
    const docRef = firestoreDoc(
      db, ...getCypAdhdReportContentPathSegments(patientId, stageKey),
    );
    updateDoc(docRef, {
      [`outputs.${spanCode}`]: newValue,
      [`approvedParagraphs.${spanCode}`]: deleteField(),
      [`editHistory.${spanCode}.${timestampKey}`]: editEntry,
      lastReviewedAt: serverTimestamp(),
      reviewedBy: auth.currentUser.uid,
      reviewedByName: auth.currentUser.displayName,
    }).then(() => {
      emitParagraphEdited({
        patientId,
        module: flagPrefix,
        docStructure,
        code: spanCode,
        prevText: previousValue,
        priorHistory: REPORT_EDIT_HISTORY[spanCode] || {},
        nextText: newValue,
      });
    }).catch(error => {
      console.error('Error updating paragraph on edit:', error);
      addReviewedCode(spanCode);
      toast.error(
        'Your edit did not save. Please try editing the paragraph again.',
        { toastId: 'paragraph-edit-save-error' }
      );
    });

    markSectionDirty();
  };

  const handleCancel = () => {
    if (state.selectedCode && preEditContent[state.selectedCode]) {
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[state.selectedCode] = preEditContent[state.selectedCode];
      }
    }
    setIsEditing(false);
    safeUpdatePosition(state.selectedCode);
  };

  const handleReset = () => {
    if (!canEdit) {
      toast.error(`Only the clinician assigned to the ${moduleTitle} module can reset these paragraphs.`);
      return;
    }
    if (!state.selectedCode || !initialContent[state.selectedCode]) return;

    const spanCode = state.selectedCode;
    const newValue = initialContent[spanCode];
    const previousValue = REPORT_DATA_OUTPUTS[spanCode];

    if (REPORT_DATA_OUTPUTS) {
      REPORT_DATA_OUTPUTS[spanCode] = newValue;
    }
    removeReviewedCode(spanCode);

    const editEntry = {
      clinicianId: auth.currentUser.uid,
      clinicianName: auth.currentUser.displayName,
      timestamp: serverTimestamp(),
      previousValue,
      newValue,
      isReset: true,
    };
    const timestampKey = Date.now().toString();

    const docRef = firestoreDoc(
      db, ...getCypAdhdReportContentPathSegments(patientId, stageKey),
    );
    updateDoc(docRef, {
      [`outputs.${spanCode}`]: newValue,
      [`approvedParagraphs.${spanCode}`]: deleteField(),
      [`editHistory.${spanCode}.${timestampKey}`]: editEntry,
      lastReviewedAt: serverTimestamp(),
      reviewedBy: auth.currentUser.uid,
      reviewedByName: auth.currentUser.displayName,
    }).then(() => {
      emitParagraphReset({
        patientId,
        module: flagPrefix,
        docStructure,
        code: spanCode,
      });
    }).catch(error => {
      console.error('Error resetting paragraph:', error);
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[spanCode] = previousValue;
      }
      addReviewedCode(spanCode);
      toast.error(
        'The paragraph reset did not save. Please try again.',
        { toastId: 'paragraph-reset-save-error' }
      );
    });

    setIsEditing(false);
    markSectionDirty();
    safeUpdatePosition(spanCode);
  };

  const hasBeenModified = (code) => {
    if (!code) return false;
    return REPORT_DATA_OUTPUTS[code] !== initialContent[code];
  };

  // Capture initial content per paragraph for modification detection/reset.
  useEffect(() => {
    if (REPORT_DATA_OUTPUTS && Object.keys(REPORT_DATA_OUTPUTS).length > 0) {
      const initialValues = {};
      Object.values(docStructure).forEach(section => {
        Object.values(section).forEach(({ code }) => {
          if (code && REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== '') {
            initialValues[code] = REPORT_DATA_OUTPUTS[code];
          }
        });
      });
      setInitialContent(initialValues);
    }
  }, [REPORT_DATA_OUTPUTS, docStructure]);

  // ── Approval write ─────────────────────────────────────────────────────
  const handleParagraphApproval = async (code, isApproving) => {
    if (!canEdit) {
      toast.error(`Only the clinician assigned to the ${moduleTitle} module can approve these paragraphs.`);
      return;
    }
    try {
      const docRef = firestoreDoc(
        db, ...getCypAdhdReportContentPathSegments(patientId, stageKey),
      );
      await updateDoc(docRef, {
        [`approvedParagraphs.${code}`]: isApproving
          ? {
              approvedAt: serverTimestamp(),
              approvedBy: auth.currentUser.uid,
              approvedByName: auth.currentUser.displayName,
              content: REPORT_DATA_OUTPUTS[code],
            }
          : deleteField(),
        lastReviewedAt: serverTimestamp(),
        reviewedBy: auth.currentUser.uid,
        reviewedByName: auth.currentUser.displayName,
      });

      emitParagraphApproval({
        patientId,
        module: flagPrefix,
        docStructure,
        code,
        assessmentType: 'cyp_adhd',
        isApproving,
      });

      markSectionDirty();
    } catch (error) {
      console.error('Error updating paragraph approval:', error);
      // Re-throw so the caller observes the failure — a silently failed
      // approval is how incomplete sections used to slip through.
      throw error;
    }
  };

  // ── Advance helpers ────────────────────────────────────────────────────
  const advanceToNextUnreviewed = (fromCode) => {
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === fromCode);
    const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
      index > currentIndex && !reviewedCodes.has(p.id)
    );

    if (nextUnreviewed && documentContainerRef.current) {
      const containerRect = documentContainerRef.current.getBoundingClientRect();
      const nextRect = nextUnreviewed.getBoundingClientRect();
      const scrollTop = documentContainerRef.current.scrollTop +
        (nextRect.top - containerRect.top) - containerRect.height / 3;
      documentContainerRef.current.scrollTo({ top: scrollTop, behavior: 'smooth' });

      setTimeout(() => {
        const nextCode = nextUnreviewed.id;
        setState(prev => ({
          ...prev,
          selectedCode: nextCode,
          panelInputs: state.modelInputsFirestore?.[nextCode] || {},
          isSidePanelOpen: true,
        }));
        setTimeout(() => safeUpdatePosition(nextCode), 0);
      }, 300);
    } else {
      setState(prev => ({
        ...prev,
        selectedCode: null,
        panelInputs: {},
        isSidePanelOpen: false,
      }));
    }
  };

  const handleMarkAsDone = async () => {
    if (!canEdit) {
      toast.error(`Only the clinician assigned to the ${moduleTitle} module can approve these paragraphs.`);
      return;
    }
    if (!state.selectedCode) return;
    const approvedCode = state.selectedCode;

    trackApproval(
      handleParagraphApproval(approvedCode, true).catch(error => {
        console.error('Error in handleParagraphApproval:', error);
        removeReviewedCode(approvedCode);
        toast.error(
          'A paragraph approval did not save and has been returned to "needs review". Please approve it again.',
          { toastId: 'paragraph-approval-save-error' }
        );
      })
    );

    addReviewedCode(approvedCode);
    advanceToNextUnreviewed(approvedCode);
  };

  const handleSkip = () => {
    if (!canEdit || !state.selectedCode) return;
    advanceToNextUnreviewed(state.selectedCode);
  };

  // ── Exit review ────────────────────────────────────────────────────────
  const handleExitReview = async () => {
    const documentsPath = `/patient/${patientId}/cyp-adhd/documents`;

    if (!canEdit) {
      if (loadedPatientId !== patientId) {
        toast.error(
          'The review is still loading. Please wait a moment and try again.',
          { toastId: 'review-exit-not-loaded' }
        );
        return;
      }
      clearPatientData();
      navigate(documentsPath);
      return;
    }

    setIsSaving(true);
    try {
      if (loadedPatientId !== patientId) {
        toast.error(
          'The review is still loading. Please wait a moment and try again.',
          { toastId: 'review-exit-not-loaded' }
        );
        setIsSaving(false);
        return;
      }

      await settlePendingApprovals();

      // Snapshot-derived approval flag on the CYP reportContent doc. The
      // '${flagPrefix}ReportApproved' flag this writes is exactly what
      // cyp_adhd_stage_progress.py reads to unlock the next stage.
      const { isFullyApproved: approvedNow, outstandingCodes, wasFirstApproval } =
        await finalizeModularSectionReview({
          patientId,
          stageKey,
          flagPrefix,
          docStructure,
          getPathSegments: getCypAdhdReportContentPathSegments,
        });

      const unpersisted = computeUnpersistedApprovals(reviewedCodes, outstandingCodes);
      if (!approvedNow && (isFullyApproved || unpersisted.length > 0)) {
        unpersisted.forEach(removeReviewedCode);
        await refreshReportContent(patientId, stageKey);
        toast.error(
          unpersisted.length > 0
            ? `${unpersisted.length} paragraph approval${unpersisted.length === 1 ? ' did' : 's did'} not save. `
              + 'The affected paragraphs have been returned to "needs review" — please approve them again.'
            : 'Some paragraphs still need review before this section can be completed. '
              + 'The page has been refreshed to show them.',
          { toastId: 'review-exit-unsaved' }
        );
        return;
      }

      appendReportAuditEntry(patientId, {
        module: flagPrefix,
        eventType: 'approved',
        isFirstOccurrence: wasFirstApproval,
      });

      // Kick off PDF/DOCX rendering of this stage's document, exactly as
      // RD_CI_Autism does: only when the section is fully approved
      // (snapshot-derived) AND something changed this session, so a no-op
      // exit never regenerates an already-current document.
      if (approvedNow && sectionDirtyRef.current) {
        TriggerRenderCypAdhdDocTemplate({
          patientId,
          stageKey,
          clinicianId: auth.currentUser.uid,
        }).catch((err) => console.error('Error triggering CYP document render:', err));
      }

      // Auto-finalise the Patient Report: if all four section reports are
      // now approved (CI + SSI + DD + PAD) and the Patient Report is stale,
      // trigger its render. Fire-and-forget — the helper reads fresh state
      // from Firestore, gates on the four-section approval, checks
      // staleness against the trigger-time anchor, and handles concurrency.
      // Mirrors the Autism review exit's call to
      // rerenderAutismPatientReportIfStale.
      if (approvedNow) {
        rerenderCypAdhdPatientReportIfStale({
          patientId,
          clinicianId: auth.currentUser.uid,
        }).catch((err) => console.error('Error in CYP patient report auto-finalise:', err));
      }

      // ── Assessment progress emails (CYP ADHD) ───────────────────
      // Fire the same callables that the adult review exits use. The
      // server-side callable handles all CYP-specific logic (separate
      // org config key, separate dedup stamp, empty patient_email, no
      // non-pharm block for PAD).
      //
      // CI and SSI exits: both trigger the MDT-information-available
      // email. The server only sends once BOTH the CI and SSI reports
      // are approved (reading the CYP reportContent docs), skipping
      // without stamping otherwise, so firing from both exits makes
      // the email order-independent: whichever report the clinician
      // approves second is the one that sends it. Mirrors RD_CI.js /
      // RD_SSI.js on the adult pathway.
      //
      // PAD exit: triggers the assessment-complete email.
      //
      // DD exit: no email trigger — there is no corresponding email.
      if (approvedNow) {
        if (stageKey === 'clinicalInterview' || stageKey === 'semiStructuredInterview') {
          TriggerSSICompletionEmail(patientId, auth.currentUser.uid);
        } else if (stageKey === 'postAssessmentDiscussion') {
          TriggerPADCompletionEmail(patientId, auth.currentUser.uid);
        }
      }

      clearPatientData();
      sectionDirtyRef.current = false;
      navigate(documentsPath);
    } catch (error) {
      console.error('Error during document review completion:', error);
      toast.error(
        'Could not save your review just now. Please check your connection and try again.',
        { toastId: 'review-exit-error' }
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ── Scroll to first unreviewed ─────────────────────────────────────────
  const goToFirstUnreviewed = () => {
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const firstUnreviewed = Array.from(allParagraphs).find(p => !reviewedCodes.has(p.id));

    if (firstUnreviewed && documentContainerRef.current) {
      const containerRect = documentContainerRef.current.getBoundingClientRect();
      const nextRect = firstUnreviewed.getBoundingClientRect();
      const scrollTop = documentContainerRef.current.scrollTop +
        (nextRect.top - containerRect.top) - containerRect.height / 3;
      documentContainerRef.current.scrollTo({ top: scrollTop, behavior: 'smooth' });

      setTimeout(() => {
        handleParagraphClick({ currentTarget: firstUnreviewed }, firstUnreviewed.id);
      }, 300);
    }
  };

  const calculateProgress = useCallback(() => {
    if (totalParagraphs === 0) return 0;
    return Math.round((reviewedCodes.size / totalParagraphs) * 100);
  }, [reviewedCodes.size, totalParagraphs]);

  // ── Effects ────────────────────────────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const inputs = reportDoc?.inputs;
    if (inputs) {
      setState(prev => ({ ...prev, modelInputsFirestore: inputs }));
    }
  }, [reportDoc]);

  useEffect(() => {
    const currentContainer = documentContainerRef.current;
    if (isEditing && currentContainer) {
      const originalOverflow = currentContainer.style.overflow;
      currentContainer.style.overflow = 'auto';
      const handleWheel = (e) => e.stopPropagation();
      currentContainer.addEventListener('wheel', handleWheel, { passive: true });
      return () => {
        currentContainer.removeEventListener('wheel', handleWheel);
        currentContainer.style.overflow = originalOverflow;
      };
    }
  }, [isEditing]);

  const handlePageClick = useCallback((event) => {
    if (isEditing) return;
    if (
      event.target.closest('[data-paragraph-container]') ||
      event.target.closest('[data-action-panel]')
    ) {
      return;
    }
    setIsEditing(false);
    setState(prev => ({
      ...prev,
      selectedCode: null,
      panelInputs: {},
      isSidePanelOpen: false,
    }));
  }, [isEditing]);

  useEffect(() => {
    const container = documentContainerRef.current;
    if (container) {
      container.addEventListener('click', handlePageClick);
    }
    return () => {
      if (container) {
        container.removeEventListener('click', handlePageClick);
      }
    };
  }, [handlePageClick]);

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <Layout activePage="dashboard" scrollable={false}>
      <InitialWarningDialog
        showInitialWarning={showInitialWarning}
        setShowInitialWarning={setShowInitialWarning}
      />
      <div className="h-full flex flex-col overflow-hidden">
        {isEditing && (
          <div
            className="fixed inset-0 bg-gray-900/20 z-10 transition-opacity"
            style={{ pointerEvents: 'none' }}
          />
        )}

        <div className="flex-none pt-4">
          <DocumentReviewHeader
            title={`Reviewing: ${moduleTitle}`}
            reviewedCodes={reviewedCodes}
            totalParagraphs={totalParagraphs}
            isSaving={isSaving}
            handleExitReview={handleExitReview}
            goToFirstUnreviewed={goToFirstUnreviewed}
            calculateProgress={calculateProgress}
          />
          <WarningBar show={showWarning} />
          {!canEdit && header && (
            <div className="mt-2 bg-gray-100 border border-gray-300 rounded-lg px-4 py-2 text-sm text-gray-700">
              Read-only view. Only{assignedClinicianName ? ` ${assignedClinicianName}, the clinician` : ' the clinician'} assigned to the {moduleTitle} module can review, edit, and approve these paragraphs.
            </div>
          )}
        </div>

        <div className="flex-1 px-8 pb-8 min-h-0 relative">
          <div className="document-display-container h-full overflow-hidden bg-slate-800">
            <div className="flex h-full">
              <div
                ref={documentContainerRef}
                className={`
                  w-1/2 relative m-4 p-16 border shadow-md bg-white
                  ${isEditing ? 'overflow-hidden' : 'overflow-y-auto'}
                `}
              >
                <div className="space-y-8 text-left">
                  {Object.entries(docStructure).map(([title, paragraphs]) => (
                    <Section key={title} title={title} paragraphs={paragraphs} />
                  ))}
                </div>
                <div className="h-[calc(100vh-32rem)]" aria-hidden="true" />

                <FloatingActionPanel
                  panelRef={panelRef}
                  position={panelPosition}
                  visible={canEdit && Boolean(state.selectedCode)}
                  isEditing={isEditing}
                  hasBeenModified={hasBeenModified(state.selectedCode)}
                  onStartEdit={handleStartEdit}
                  onSave={handleSaveEdit}
                  onCancel={handleCancel}
                  onReset={handleReset}
                  onApprove={handleMarkAsDone}
                  onSkip={handleSkip}
                />
              </div>

              <div className={`w-2/5 p-4 flex flex-col ${!state.selectedCode ? 'hidden' : ''}`}>
                <div className="flex-1 text-sm bg-gray-100 rounded-xl overflow-y-auto border shadow-sm flex flex-col h-full">
                  {state.selectedCode && (
                    <div className="flex flex-col h-full p-6 overflow-hidden">
                      <div className="flex-1 flex flex-col min-h-0">
                        <div className="flex-none flex justify-between items-center">
                          <p className="text-xl font-bold text-gray-800 mb-2">AI Inputs</p>
                        </div>
                        <div className="overflow-y-auto">
                          {/* Cards follow the proforma. CI keys are question positions.
                              SSI keys are the interview order: six-month and
                              onset, then the settings chips, then Home
                              examples, then Education examples. The Home and
                              Education "Met" cards are the same chips, so
                              they are left out. Adult pages do not pass this
                              sort and are unchanged. */}
                          {inputPanelGroups.map(([label, inputsKey]) => (
                            <React.Fragment key={inputsKey}>
                              {renderModelInput(label, omitCypSsiEnvironmentMet(state.panelInputs?.[inputsKey]), {
                                sortEntries: compareStructuredEntryKeys,
                              })}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
